<template>
  <!-- Follow up > Emails Sent (item (e), batch 3; on the Follow up page since 04/10/2026).
       The standard list design (MyActivityTableForLazyLoadingWithResize, used by Events and All
       Activities): toolbar with refresh and Global Search, a striped grid with resizable, sortable
       columns, the frozen Actions column, the 25/50/100 pager. Paged, searched and sorted on the
       server; the filters sit in a row under the column headers. -->
  <div class="followup-list">
    <p class="followup-explain">The automatic follow-up emails that went to customers (and any no-reply emails), newest first.
      <span v-if="data && !data.allUsers">You see the quotes you follow up.</span></p>

    <Toolbar class="p-mb-4">
      <template slot="left">
        <div class="p-inputgroup">
          <Button id="sentRefresh" icon="pi pi-fw pi-refresh" class="p-mr-1" @click="load" />
          <InputText id="sentSearch" v-model="searchText" placeholder="Global Search" @input="searchSoon" @keyup.enter="search" />
          <Button icon="pi pi-times p-pr-1" class="p-mr-1" @click="clearSearch" />
        </div>
      </template>
      <template slot="right">
        <Button v-if="filtered" id="sentClearFilters" label="Clear filters" icon="pi pi-filter-slash"
          class="p-button-text" @click="clear" />
      </template>
    </Toolbar>

    <OverlayPanel ref="opActions" appendTo="body" :showCloseIcon="true" style="width: 250px;">
      <div v-if="actionRow" class="followup-actions">
        <Button id="sentViewAction" label="View the email" icon="pi pi-eye" class="p-button-text p-button-plain"
          @click="view(actionRow)" />
        <Button id="sentOpenQuote" label="Open the quote's follow-up" icon="pi pi-external-link"
          class="p-button-text p-button-plain" @click="openQuote(actionRow)" />
      </div>
    </OverlayPanel>

    <DataTable
      :value="rows"
      :paginator="true"
      class="p-datatable-striped p-datatable-sm p-datatable-gridlines p-datatable-customers"
      dataKey="logId"
      :rowHover="true"
      :loading="loading"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :rowsPerPageOptions="[25, 50, 100]"
      currentPageReportTemplate="{first} to {last} of {totalRecords} Emails"
      :resizableColumns="true"
      columnResizeMode="expand"
      :lazy="true"
      :totalRecords="total"
      :rows="size"
      :first="page * size"
      @page="onPage"
      @sort="onSort"
      :sortField="sortField"
      :sortOrder="sortOrder"
      :scrollable="true"
      scrollHeight="400px"
      frozenWidth="50px"
    >
      <template #empty>No follow-up emails were sent for this search.</template>
      <template #loading>Loading the sent emails. Please wait.</template>

      <Column header="Actions" headerStyle="width: 50px; height: 41px; padding: 10px 0px;" :frozen="true">
        <template #filter><div class="filter-cell"><span class="filter-spacer"></span></div></template>
        <template #body="slot">
          <div class="cell">
            <Button :id="'sentActions' + slot.data.logId" icon="pi pi-ellipsis-h"
              class="p-button-rounded p-button-text" @click="toggleActions($event, slot.data)" />
          </div>
        </template>
      </Column>
      <Column field="sentAt" header="Sent" :sortable="true" headerStyle="width: 150px; height: 41px;">
        <template #filter><div class="filter-cell">
          <div class="date-range">
            <Calendar id="sentFrom" v-model="form.from" dateFormat="dd/mm/yy" placeholder="From" :showButtonBar="true" />
            <Calendar id="sentTo" v-model="form.to" dateFormat="dd/mm/yy" placeholder="To" :showButtonBar="true" />
          </div>
        </div></template>
        <template #body="slot"><div class="cell">{{ slot.data.sentAt }}</div></template>
      </Column>
      <Column field="quoteRef" header="Quote" :sortable="true" headerStyle="width: 120px; height: 41px;">
        <template #filter><div class="filter-cell">
          <InputText id="sentQuoteNo" v-model="form.quoteNo" class="p-column-filter" placeholder="Quote number"
            @input="searchSoon" @keyup.enter="search" />
        </div></template>
        <template #body="slot">
          <div class="cell">
            <router-link :to="'/enquiries/' + slot.data.oppId + '/followup'">{{ slot.data.quoteRef }}</router-link>
          </div>
        </template>
      </Column>
      <Column field="customerName" header="Customer" :sortable="true" headerStyle="width: 200px; height: 41px;">
        <template #filter><div class="filter-cell">
          <InputText id="sentCustomer" v-model="form.customer" class="p-column-filter" placeholder="Customer"
            @input="searchSoon" @keyup.enter="search" />
        </div></template>
        <template #body="slot"><div class="cell"><span class="clamp" :title="slot.data.customerName">{{ slot.data.customerName }}</span></div></template>
      </Column>
      <Column field="contactName" header="Contact" :sortable="true" headerStyle="width: 170px; height: 41px;">
        <template #filter><div class="filter-cell">
          <InputText id="sentContact" v-model="form.contact" class="p-column-filter" placeholder="Contact"
            @input="searchSoon" @keyup.enter="search" />
        </div></template>
        <template #body="slot"><div class="cell"><span class="clamp" :title="slot.data.contactName">{{ slot.data.contactName }}</span></div></template>
      </Column>
      <Column field="subject" header="Email" :sortable="true" headerStyle="width: 280px; height: 41px;">
        <template #filter><div class="filter-cell"><span class="filter-spacer"></span></div></template>
        <template #body="slot">
          <div class="cell"><span class="clamp" :title="slot.data.what + ': ' + slot.data.subject">{{ slot.data.what }}: {{ slot.data.subject }}</span></div>
        </template>
      </Column>
      <Column field="actualTo" header="To" :sortable="true" headerStyle="width: 230px; height: 41px;">
        <template #filter><div class="filter-cell"><span class="filter-spacer"></span></div></template>
        <template #body="slot">
          <!-- An address is never broken mid-word: one line each, the whole of it on hover. -->
          <div class="cell" :title="to(slot.data)">
            <div class="no-break sent-to">{{ slot.data.actualTo }}</div>
            <div v-if="meantFor(slot.data)" class="no-break"><small>meant for {{ meantFor(slot.data) }}</small></div>
          </div>
        </template>
      </Column>
      <Column field="salespersonName" header="Salesperson" :sortable="true" headerStyle="width: 170px; height: 41px;">
        <template #filter><div class="filter-cell">
          <Dropdown v-if="!data || data.allUsers" id="sentUser" v-model="form.userId" :options="userOptions"
            optionLabel="name" optionValue="id" :filter="true" placeholder="Everyone" class="p-column-filter" />
          <span v-else class="filter-spacer"></span>
        </div></template>
        <template #body="slot"><div class="cell">{{ slot.data.salespersonName }}</div></template>
      </Column>
      <Column header="" headerStyle="width: 90px; height: 41px;">
        <template #filter><div class="filter-cell"><span class="filter-spacer"></span></div></template>
        <template #body="slot">
          <div class="cell">
            <Button :id="'viewSent' + slot.data.logId" label="View" icon="pi pi-eye" class="p-button-text p-button-sm"
              @click="view(slot.data)" />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog :visible.sync="viewing" header="Follow-up email as sent" :modal="true" :style="{ width: '720px' }"
      :contentStyle="{ maxHeight: '70vh', overflowY: 'auto' }">
      <div v-if="email" id="sentEmailView">
        <p class="p-mt-0"><b>Sent:</b> {{ email.sentAt }}<br /><b>To:</b> {{ to(email) }}<br /><b>Quote:</b> {{ email.quoteRef }}
          ({{ email.what }})<br /><b>Subject:</b> {{ email.subject }}</p>
        <pre class="sent-body">{{ email.body }}</pre>
      </div>
    </Dialog>
  </div>
</template>

<script>
import FollowupService, { errorMessage } from '@/service/FollowupService'
import UserService from '@/service/UserService'
import { sentListBody, sentTo, meantFor } from './sentEmails'

const empty = () => ({ from: null, to: null, customer: '', contact: '', quoteNo: '', userId: null })

// Typing in Global Search or a text filter searches once the typing stops.
export const SEARCH_DELAY_MS = 400

export default {
  // item (e), batch 3: each user sees the quotes they follow up; Admin sees all and can pick a salesperson
  name: 'SentFollowupEmails',
  data: () => ({ followupService: new FollowupService(), form: empty(), data: null, page: 0, size: 25, loading: false,
    users: [], viewing: false, email: null, searchText: '', sortField: null, sortOrder: null, actionRow: null,
    timer: null }),
  computed: {
    rows() { return (this.data && this.data.rows) || [] },
    total() { return (this.data && this.data.total) || 0 },
    userOptions() {
      return [{ id: null, name: 'Everyone' }].concat(this.users.map(u => ({ id: u.id, name: [u.firstName, u.lastName].filter(Boolean).join(' ') })))
    },
    filtered() {
      var f = this.form
      return !!(f.from || f.to || f.customer || f.contact || f.quoteNo || f.userId !== null)
    }
  },
  watch: {
    // A date or salesperson picked searches straight away; text boxes wait for the typing to stop.
    'form.from'() { this.search() },
    'form.to'() { this.search() },
    'form.userId'() { this.search() }
  },
  created() {
    new UserService().getAllActiveUsers().then(d => { this.users = d || [] }).catch(() => {})
    this.load()
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  },
  methods: {
    to: sentTo,
    meantFor,
    body() {
      return sentListBody(this.form, this.page, this.size, { search: this.searchText, sortField: this.sortField, sortOrder: this.sortOrder })
    },
    load() {
      clearTimeout(this.timer)
      this.loading = true
      return this.followupService.sentEmails(this.body())
        .then(d => { this.data = d })
        .catch(err => {
          this.$toast.add({ severity: 'error', summary: 'Not loaded', detail: errorMessage(err, 'The sent emails could not be loaded.'), life: 5000 })
        })
        .finally(() => { this.loading = false })
    },
    search() {
      this.page = 0
      return this.load()
    },
    searchSoon() {
      clearTimeout(this.timer)
      this.timer = setTimeout(() => this.search(), SEARCH_DELAY_MS)
    },
    clearSearch() {
      this.searchText = ''
      return this.search()
    },
    clear() {
      this.form = empty()
      return this.search()
    },
    onPage(e) {
      this.page = e.page
      this.size = e.rows
      return this.load()
    },
    onSort(e) {
      this.sortField = e.sortField || null
      this.sortOrder = e.sortOrder || null
      this.page = 0
      return this.load()
    },
    toggleActions(event, row) {
      this.actionRow = row
      this.$refs.opActions.toggle(event)
    },
    openQuote(row) {
      if (this.$refs.opActions && this.$refs.opActions.hide) this.$refs.opActions.hide()
      this.$router.push('/enquiries/' + row.oppId + '/followup').catch(() => {})
    },
    view(row) {
      if (this.$refs.opActions && this.$refs.opActions.hide) this.$refs.opActions.hide()
      return this.followupService.sentEmail(row.logId)
        .then(d => { this.email = d; this.viewing = true })
        .catch(err => {
          this.$toast.add({ severity: 'error', summary: 'Not loaded', detail: errorMessage(err, 'The email could not be loaded.'), life: 5000 })
        })
    }
  }
}
</script>

<style scoped>
.followup-explain { margin: 1rem 0 0.75rem; }
.cell { height: 52px; padding: 4px 0px; display: flex; flex-direction: column; justify-content: center; overflow: hidden; }
/* Every row the same height, so the frozen Actions column stays level with the rest; a long text shows two lines and the whole of it on hover. */
.clamp { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
/* An address is never broken mid-word: one line, cut with an ellipsis, the whole of it on hover. */
.no-break { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.date-range { display: flex; flex-direction: column; gap: 4px; }
.date-range ::v-deep .p-calendar, .date-range ::v-deep input, .p-column-filter, ::v-deep .p-column-filter input { width: 100%; }
/* Filter cells one fixed height, so the frozen Actions header lines up with the rest. */
.filter-cell { height: 72px; display: flex; flex-direction: column; justify-content: center; }
.filter-spacer { display: block; height: 1px; }
/* The empty / loading message shows once, in the scrolling part, not cut off under the frozen Actions column too. */
::v-deep .p-datatable-frozen-view .p-datatable-emptymessage td { color: transparent; }
.sent-body { white-space: pre-wrap; font-family: inherit; background: #f8f9fa; padding: 0.75rem; border-radius: 4px; }
</style>
